import { z } from 'zod';

import { PaginationResponseSchema } from '../../../../shared/global.schema';

export const ProductVariationItem = z.object({
    type_id: z.string(),
    unit_id: z.string(),
    strength_id: z.string(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
    product_id: z.string(),
});

export type ProductDto = z.infer<typeof ProductItems>;
export type ProductListDto = z.infer<typeof ProductList>;
export type ProductVariationDto = z.infer<typeof ProductItems>;

const StrengthSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    name: z.string(),
});

const TypeSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    name: z.string(),
    status: z.boolean(),
});

const UnitSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    name: z.string(),
    quantity: z.number(),
    strip_quantity: z.number().nullable(),
});

const VariationSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    product_id: z.string(),
    unit_id: z.string(),
    strength_id: z.string(),
    type_id: z.string(),
    alert_quantity: z.number(),
    purchase_price: z.number(),
    wholesale_price: z.number(),
    retail_price: z.number(),
    strength: StrengthSchema,
    type: TypeSchema,
    unit: UnitSchema,
});

const BrandSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    name: z.string(),
    description: z.string().optional().nullable(),
});

const GenericSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    name: z.string(),
    indications: z.string().optional().nullable(),
});

export const SingleItemSchema = z.object({
    id: z.string(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    name: z.string(),
    shelf: z.string().optional().nullable(),
    alert_quantity: z.number(),
    generic_id: z.string(),
    brand_id: z.string(),
    brand: BrandSchema,
    generic: GenericSchema,
    variations: z.array(VariationSchema),
});

export const SignleProductSchema = z.object({
    data: SingleItemSchema,
    message: z.string(),
    statusCode: z.number(),
});
export const ProductItems = z.object({
    name: z.string(),
    shelf: z.string().optional().nullable(),
    brand_id: z.string(),
    generic_id: z.string(),
    retail_price: z.number().optional(),
    wholesale_price: z.number().optional(),
    purchase_price: z.number().optional(),
    generic: z.string(),
    brand: z.string(),
    id: z.string(),
    product_id: z.string(),
});
export const ProductList = z.object({
    data: z.array(ProductItems),
    message: z.string(),
    pagination: PaginationResponseSchema,
});
export type SingleProductDto = z.infer<typeof SingleItemSchema>;
export type VariationDto = z.infer<typeof VariationSchema>;

export const ProductV2 = z.object({
    id: z.string(),
    created_at: z.string(),
    updated_at: z.string().nullable(),
    product_id: z.string(),
    alert_quantity: z.coerce.number(),
    purchase_price: z.coerce.number(),
    wholesale_price: z.coerce.number(),
    retail_price: z.coerce.number(),
    brand_id: z.string(),
    brand_name: z.string(),
    generic_id: z.string(),
    generic_name: z.string(),
    strength_id: z.string(),
    strength_name: z.string(),
    type_id: z.string(),
    type_name: z.string(),
    unit_id: z.string(),
    unit_name: z.string(),
    full_product_name: z.string(),
});

export type TProductV2 = z.infer<typeof ProductV2>;

export const ProductListResponseV2 = z.object({
    data: z.array(ProductV2),
    message: z.string(),
    pagination: PaginationResponseSchema,
});

export type TProductListResponseV2 = z.infer<typeof ProductListResponseV2>;
